import { useTranslation } from "react-i18next";
import type { AddressResponse } from "../../api/addresses/types";
import { deliveryMatchesSelectedFlyerType, type DeliveryResponse } from "../../api/deliveries";

/** Same rule for showing "Ångra utdelning" in context menu and bulk panel. */
export function canUndoAddressMapDelivery(
    selectedFlyerTypeId: string | null | undefined,
        onUndoDelivery: (() => void | Promise<void>) | undefined,
    deliveries: DeliveryResponse[],
    currentUserId: string | undefined
): boolean {
    return !!(
        selectedFlyerTypeId &&
        onUndoDelivery &&
        deliveries.some(
            (d) =>
                deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId) &&
                (!currentUserId || d.recordedByUserId === currentUserId)
        )
    );
}

export function buildAddressGoogleMapsSearchUrl(address: AddressResponse): string {
    const query = [address.street, address.number, address.apartment, address.postalCode, address.city]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
        .join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export type AddressMapActionListVariant = "context-menu" | "panel";

export interface AddressMapActionListProps {
    variant: AddressMapActionListVariant;
    address: AddressResponse;
    googleMapsUrl: string;
    isLoading: boolean;
    markDeliveredDisabled: boolean;
    canUndo: boolean;
    /** Called after most actions (close context menu or clear panel selection). */
    dismissAfterAction: () => void;
    /** When true, delivery history does not dismiss the host (panel stays open). */
    keepHostOpenOnDeliveryHistory: boolean;
    /** Context menu closes after mark; bulk panel keeps selection until confirm (opens dialog first). */
    dismissAfterMarkDelivered: boolean;
    onDeliveryHistory?: () => void;
    onMarkDelivered?: () => void;
    onUndoDelivery?: () => void | Promise<void>;
    onHide?: () => void;
    onBlock?: () => void;
    onMove?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
}

/**
 * Shared address actions for the map context menu and the single-address bulk panel.
 * Order and visibility rules stay in one place.
 */
export const AddressMapActionList = ({
    variant,
    address,
    googleMapsUrl,
    isLoading,
    markDeliveredDisabled,
    canUndo,
    dismissAfterAction,
    keepHostOpenOnDeliveryHistory,
    dismissAfterMarkDelivered,
    onDeliveryHistory,
    onMarkDelivered,
    onUndoDelivery,
    onHide,
    onBlock,
    onMove,
    onEdit,
    onCopy
}: AddressMapActionListProps): JSX.Element => {
    const { t } = useTranslation();

    const btnClass =
        variant === "context-menu" ? "address-context-menu__item" : "bulk-address-panel__context-button";
    const linkClass =
        variant === "context-menu"
            ? "address-context-menu__item address-context-menu__item--link"
            : "bulk-address-panel__context-button bulk-address-panel__context-button--link";

    const fire = (fn: () => void) => {
        fn();
        dismissAfterAction();
    };

    return (
        <>
            {onDeliveryHistory && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        onDeliveryHistory();
                        if (!keepHostOpenOnDeliveryHistory) {
                            dismissAfterAction();
                        }
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.deliveryHistory")}
                </button>
            )}

            {onMarkDelivered && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        onMarkDelivered();
                        if (dismissAfterMarkDelivered) {
                            dismissAfterAction();
                        }
                    }}
                    disabled={isLoading || markDeliveredDisabled}
                >
                    {t("map.addressContextMenu.markDelivered")}
                </button>
            )}

            {canUndo && onUndoDelivery && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        void Promise.resolve(onUndoDelivery()).finally(() => dismissAfterAction());
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.undoDelivery")}
                </button>
            )}

            {onHide && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onHide)}
                    disabled={isLoading}
                >
                    {address.isHidden
                        ? t("map.addressContextMenu.showAddress")
                        : t("map.addressContextMenu.hideAddress")}
                </button>
            )}

            {onBlock && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onBlock)}
                    disabled={isLoading}
                >
                    {address.isBlocked
                        ? t("map.addressContextMenu.unblockAddress")
                        : t("map.addressContextMenu.blockAddress")}
                </button>
            )}

            {onMove && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onMove)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.moveAddress")}
                </button>
            )}

            {onEdit && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onEdit)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.editMetadata")}
                </button>
            )}

            {onCopy && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onCopy)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.copyAddress")}
                </button>
            )}

            <a
                className={linkClass}
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismissAfterAction}
            >
                {t("map.addressContextMenu.openInGoogleMaps")}
            </a>
        </>
    );
};
